import { useLiveQuery } from "dexie-react-hooks";
import { HomeTabs } from "../components/HomeTabs";
import { Icon } from "../components/Icon";
import { plural } from "../components/ui";
import { db } from "../lib/db";
import { deleteSet, localStats } from "../lib/repo";
import { useState } from "react";
import { navigate } from "../lib/router";
import { offlineReadiness } from "../lib/tts";
import { Composer } from "./Compose";

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Доброй ночи";
  if (h < 12) return "Доброе утро";
  if (h < 18) return "Добрый день";
  return "Добрый вечер";
}

export function Library() {
  const sets = useLiveQuery(async () => {
    const all = (await db.sets.orderBy("updatedAt").reverse().toArray()).filter((s) => !s.deletedAt);
    return Promise.all(
      all.map(async (s) => {
        const affs = (await db.affirmations.where("setId").equals(s.id).toArray())
          .filter((a) => !a.deletedAt)
          .sort((a, b) => a.position - b.position);
        const ready = await offlineReadiness(affs.map((a) => ({ text: a.text, voiceId: s.voiceId, preset: s.preset })));
        return { set: s, first: affs[0]?.text, count: affs.length, offline: ready.total > 0 && ready.ready === ready.total };
      }),
    );
  });
  const stats = useLiveQuery(localStats);
  const [confirming, setConfirming] = useState<string | null>(null);

  if (sets === undefined) return <main className="screen" aria-busy="true" />;

  if (sets.length === 0) {
    return (
      <main className="screen">
        <header className="home-head">
          <span className="wordmark">Mantra</span>
          <button className="icon-btn" onClick={() => navigate({ name: "settings" })} aria-label="Настройки">
            <Icon name="settings" />
          </button>
        </header>
        <HomeTabs active="sets" />
        <p className="lede">
          Напишите своими словами, чего вы хотите. Мы соберём из этого аффирмации и проговорим их спокойным голосом.
        </p>
        <Composer />
      </main>
    );
  }

  return (
    <main className="screen">
      <header className="home-head">
        <span className="wordmark">Mantra</span>
        <button className="icon-btn" onClick={() => navigate({ name: "settings" })} aria-label="Настройки">
          <Icon name="settings" />
        </button>
      </header>
      <HomeTabs active="sets" />

      <section className="hello">
        <h1>{greeting()}</h1>
        {stats && stats.sessions > 0 && (
          <p className="muted">
            {stats.streak > 0 ? `${plural(stats.streak, "день", "дня", "дней")} подряд, ` : ""}
            {stats.minutes > 0
              ? `${plural(stats.minutes, "минута", "минуты", "минут")} практики`
              : plural(stats.sessions, "сессия", "сессии", "сессий")}
          </p>
        )}
      </section>

      <button className="btn primary wide" onClick={() => navigate({ name: "new" })}>
        <Icon name="plus" /> Новый настрой
      </button>

      <ul className="set-list">
        {sets.map(({ set, first, count, offline }) => (
          <li key={set.id} className="set-item">
            {confirming === set.id ? (
              <div className="set-row set-confirm" role="alertdialog" aria-label={`Удалить «${set.title}»?`}>
                <span className="set-text">
                  <span className="set-title">Удалить «{set.title}»?</span>
                  <span className="set-meta">Фразы и настройки этого настроя пропадут на всех устройствах.</span>
                </span>
                <span className="confirm-actions">
                  <button className="btn ghost" onClick={() => setConfirming(null)}>
                    Отмена
                  </button>
                  <button
                    className="btn danger"
                    onClick={() => {
                      setConfirming(null);
                      void deleteSet(set.id);
                    }}
                  >
                    Удалить
                  </button>
                </span>
              </div>
            ) : (
              <div className="set-row-wrap">
                <button className="set-row" onClick={() => navigate({ name: "play", id: set.id })}>
                  <span className="set-text">
                    <span className="set-title">{set.title}</span>
                    {first && <span className="set-first">{first}</span>}
                    <span className="set-meta">
                      {plural(count, "фраза", "фразы", "фраз")}
                      {offline && <span className="badge">доступно офлайн</span>}
                    </span>
                  </span>
                  <span className="play-dot" aria-hidden>
                    <Icon name="play" size={18} />
                  </span>
                </button>
                <button className="icon-btn set-delete" onClick={() => setConfirming(set.id)} aria-label={`Удалить «${set.title}»`}>
                  <Icon name="trash" size={18} />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
